/**
 * @graphql agg
 */
import { graphql } from 'react-relay';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import { StudioWorkspaceAri } from '@atlassian/ari/studio/workspace';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useStudioPermissionsLinkedWorkspaceQuery$data,
	useStudioPermissionsLinkedWorkspaceQuery$variables,
} from './__generated__/useStudioPermissionsLinkedWorkspaceQuery.graphql.ts';
import type {
	useStudioPermissionsQuery$data,
	useStudioPermissionsQuery$variables,
} from './__generated__/useStudioPermissionsQuery.graphql.ts';
import type {
	useStudioPermissionsWorkspaceCapabilitiesQuery$data,
	useStudioPermissionsWorkspaceCapabilitiesQuery$variables,
} from './__generated__/useStudioPermissionsWorkspaceCapabilitiesQuery.graphql.ts';
import { useStudioSpaceAri } from './useStudioSpaceAri.ts';
import { getUserAri } from '../utils/ari.ts';

type UseStudioPermissionsResponse = {
	canCreateStudioAutomation: boolean;
	isLoading: boolean;
};

type UseStudioPermissionsProps = {
	skip?: boolean;
};

// Both must be granted on the linked Studio space to build an automation there:
// the workspace-scoped `manage-automation` and the space-scoped `create-element`.
const STUDIO_MANAGE_AUTOMATION_PERMISSION = 'workspace/manage-automation';
const STUDIO_CREATE_ELEMENT_PERMISSION = 'space/create-element';

const isPermitted = (
	data: useStudioPermissionsQuery$data | null | undefined,
	permissionId: string,
): boolean =>
	data?.bulkPermitted?.some(
		(row) => row?.permissionId === permissionId && row?.permitted === true,
	) ?? false;

const getStudioWorkspaceLookupSiteAri = (cloudId: string | null | undefined): string | null => {
	if (!cloudId) {
		return null;
	}
	try {
		return ConfluenceSiteAri.create({ siteId: cloudId }).toString();
	} catch {
		return null;
	}
};

// A site is linked to at most one Studio workspace.
const getLinkedStudioWorkspaceAri = (
	data: useStudioPermissionsLinkedWorkspaceQuery$data | null | undefined,
): StudioWorkspaceAri | null => {
	const workspaceAri = data?.admin_unitLinkedResource?.links?.edges?.[0]?.node?.id;
	return workspaceAri && StudioWorkspaceAri.check(workspaceAri)
		? StudioWorkspaceAri.parse(workspaceAri)
		: null;
};

/**
 * Reusable check for whether the current user can build automations in Atlassian
 * Studio.
 */
export const useStudioPermissions = ({
	skip = false,
}: UseStudioPermissionsProps = {}): UseStudioPermissionsResponse => {
	const { cloudId, userId, isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();
	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const hasBaseEligibility = Boolean(isRovoLLMEnabled && !isAnonymousUser && cloudId);

	const { studioSpaceAri, isLoading: isStudioSpaceLoading } = useStudioSpaceAri({
		skip: skip || !hasBaseEligibility || fg('cc_automations_studio_workspace_capabilities'),
	});

	const principalId = getUserAri(userId)?.toString() ?? null;
	const resourceId = studioSpaceAri?.toString() ?? null;
	const canRunPermissionCheck = Boolean(!skip && hasBaseEligibility && principalId && resourceId);

	const { data, loading: isStudioPermissionsLoading } = useQueryInterop<
		useStudioPermissionsQuery$data,
		useStudioPermissionsQuery$variables
	>(
		graphql`
			query useStudioPermissionsQuery(
				$principalIds: [String]
				$resourceIds: [String]
				$permissionIds: [String]
				$dontRequirePrincipalsInSite: [Boolean]
			) {
				bulkPermitted(
					principalIds: $principalIds
					resourceIds: $resourceIds
					permissionIds: $permissionIds
					dontRequirePrincipalsInSite: $dontRequirePrincipalsInSite
				) {
					permissionId
					permitted
				}
			}
		`,
		{
			skip: !canRunPermissionCheck || fg('cc_automations_studio_workspace_capabilities'),
			variables: {
				principalIds: [principalId, principalId],
				resourceIds: [resourceId, resourceId],
				permissionIds: [STUDIO_MANAGE_AUTOMATION_PERMISSION, STUDIO_CREATE_ELEMENT_PERMISSION],
				dontRequirePrincipalsInSite: [true, true],
			},
			fetchPolicy: 'cache-first',
			onError: (error) => {
				if (isStatusCodeError(error, '403') || isStatusCodeError(error, '404')) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	const siteAri = getStudioWorkspaceLookupSiteAri(cloudId);
	const canRunWorkspaceLookup = Boolean(!skip && hasBaseEligibility && siteAri);

	const { data: workspaceData, loading: isStudioWorkspaceLoading } = useQueryInterop<
		useStudioPermissionsLinkedWorkspaceQuery$data,
		useStudioPermissionsLinkedWorkspaceQuery$variables
	>(
		graphql`
			query useStudioPermissionsLinkedWorkspaceQuery($siteAri: ID!) {
				admin_unitLinkedResource(id: $siteAri) {
					links(first: 1, filter: { atis: ["ati:cloud:studio:workspace"] }) {
						edges {
							node {
								id
							}
						}
					}
				}
			}
		`,
		{
			skip: !canRunWorkspaceLookup || !fg('cc_automations_studio_workspace_capabilities'),
			variables: {
				siteAri: siteAri ?? '',
			},
			fetchPolicy: 'cache-first',
			interopApolloAggClient: true,
			onError: (error) => {
				if (isStatusCodeError(error, '403') || isStatusCodeError(error, '404')) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	const workspaceId = canRunWorkspaceLookup
		? (getLinkedStudioWorkspaceAri(workspaceData)?.toString() ?? null)
		: null;
	const canRunCapabilitiesCheck = Boolean(!skip && hasBaseEligibility && workspaceId);

	const { data: capabilitiesData, loading: isStudioCapabilitiesLoading } = useQueryInterop<
		useStudioPermissionsWorkspaceCapabilitiesQuery$data,
		useStudioPermissionsWorkspaceCapabilitiesQuery$variables
	>(
		graphql`
			query useStudioPermissionsWorkspaceCapabilitiesQuery($workspaceId: ID!) {
				studio_workspaceCapabilities(workspaceId: $workspaceId)
					@optIn(to: "StudioWorkspaceCapabilities") {
					canCreateAutomationsInDefaultSpace
				}
			}
		`,
		{
			skip: !canRunCapabilitiesCheck || !fg('cc_automations_studio_workspace_capabilities'),
			variables: {
				workspaceId: workspaceId ?? '',
			},
			fetchPolicy: 'cache-first',
			interopApolloAggClient: true,
			onError: (error) => {
				if (isStatusCodeError(error, '403') || isStatusCodeError(error, '404')) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	const canCreateStudioAutomation = fg('cc_automations_studio_workspace_capabilities')
		? capabilitiesData?.studio_workspaceCapabilities?.canCreateAutomationsInDefaultSpace === true
		: isPermitted(data, STUDIO_MANAGE_AUTOMATION_PERMISSION) &&
			isPermitted(data, STUDIO_CREATE_ELEMENT_PERMISSION);

	return {
		canCreateStudioAutomation,
		isLoading:
			isStudioSpaceLoading ||
			isStudioPermissionsLoading ||
			isStudioWorkspaceLoading ||
			isStudioCapabilitiesLoading,
	};
};
